import type { MultipleOpportunity, SingleOpportunity } from '@ps/isp-web-services-types';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { useTrackers } from '@ps/web-shared-analytics';
import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import { RequestError } from '@swf/core/errors/RequestError';
import type { BetSlipState, BettableOpportunities } from '@swf/core/models/sports/betSlip/types';
import {
  getAvailableMultipleOpportunities,
  getAvailableSingleOpportunities,
  getMinOddsRestrictedSingleOpportunities
} from '@swf/core/models/sports/betSlip/utils/getAvailableOpportunities';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';

import type { FormData } from '../types';
import { buildBetDefinitions } from '../utils/buildBetDefinitions';
import { mapBetLevelErrors } from '../utils/mapBetLevelErrors';

import useRequestBetPlacement from './useRequestBetPlacement';

export const BET_DELAY_THRESHOLD = 1;

const getBettableOpportunities = (
  opportunities: BetSlipState['opportunities'],
  formValues: FormData
): BettableOpportunities => {
  const predicate = (opp: SingleOpportunity | MultipleOpportunity) =>
    !!Number(formValues[opp.id]?.stakePerLineParsed);

  return {
    SINGLE: getAvailableSingleOpportunities(opportunities.SINGLE.filter(predicate)),
    MULTIPLE: getAvailableMultipleOpportunities(opportunities.MULTIPLE.filter(predicate))
  };
};

const getMaxBetDelay = (opportunities: BetSlipState['opportunities']) =>
  Math.max(
    ...[
      ...getAvailableSingleOpportunities(opportunities.SINGLE),
      ...getAvailableMultipleOpportunities(opportunities.MULTIPLE)
    ].map((o) => o.betDelay)
  );

const isNetworkOrGeneralFailure = (error: Error) => {
  const isNetworkFailure = !(error as RequestError).headers?.statusCode;

  let isGeneralFailure = false;
  if (error instanceof RequestError) {
    isGeneralFailure =
      error.body?.detail?.FixedOddsBettingTransactionalException?.errorCode === 'GENERAL_FAILURE';
  }

  return isNetworkFailure || isGeneralFailure;
};

/*
 * Generates a bet placement and sends request to SPB
 * and adds receipt to redux
 */
const usePlaceBet = () => {
  const opportunities = useSelector(select.betSlip.opportunities);
  const singleOpportunities = useSelector(select.betSlip.singleOpportunities);
  const hasAutoAcceptEnabled = useSelector(select.account.hasAutoAcceptEnabled);
  const currency = useSelector(select.currency.get);
  const dispatch = useDispatch<Dispatch>();
  const placeBet = useRequestBetPlacement();
  const customerRef = useRef(uuidv4());
  const wallets = useSelector(select.betSlip.wallets);
  const siteId = useSelector(select.cms.getSiteId);
  const bannedByRegulator = useSelector(select.account.bannedByRegulator);
  const { betPlaced, betPlacementException, betPlacementError } = useTrackers();

  return async (formValues: FormData) => {
    // Build up the request body
    const bettableOpportunities = getBettableOpportunities(opportunities, formValues);

    const betDefinitions = [
      ...buildBetDefinitions({
        opportunities: bettableOpportunities.SINGLE,
        singleOpportunities: bettableOpportunities.SINGLE,
        formValues,
        wallets
      }),
      ...buildBetDefinitions({
        opportunities: bettableOpportunities.MULTIPLE,
        singleOpportunities: [
          ...getAvailableSingleOpportunities(opportunities.SINGLE),
          ...getMinOddsRestrictedSingleOpportunities(singleOpportunities)
        ],
        formValues,
        wallets,
        bettableSingleOpportunitiesCount: bettableOpportunities.SINGLE.length
      })
    ];

    try {
      dispatch.betSlip.attemptBetPlacement();

      const maxBetDelay = getMaxBetDelay(bettableOpportunities);
      if (maxBetDelay > BET_DELAY_THRESHOLD) {
        dispatch.modal.enqueue({ path: '/sports/bet-delay' });
      }

      // Submit the bet
      const { body: response } = await placeBet({
        acceptLowerOdds: Boolean(hasAutoAcceptEnabled),
        betDefinitions,
        customerRef: customerRef.current,
        currency
      });

      if (response.respCode && response.respCode !== 'SUCCESS') {
        betPlacementError({
          response,
          bettableOpportunities,
          opportunities: getAvailableSingleOpportunities(singleOpportunities)
        });

        const hasAMLSuspendedFailure = (response.result ?? []).some(
          (bet) => bet.resultCode === 'AML_USER_SUSPENDED'
        );

        const hasBetGeneralFailure = (response.result ?? []).some(
          (bet) => bet.resultCode === 'GENERAL_FAILURE'
        );

        if (!hasBetGeneralFailure) {
          customerRef.current = uuidv4();
        }

        if (hasAMLSuspendedFailure) {
          dispatch.betSlip.betPlacementFailed({ errorCode: 'AML_USER_SUSPENDED' });
          return mapBetLevelErrors(response.result, betDefinitions, siteId);
        }

        if (bannedByRegulator && siteId === Sites.ROM) {
          dispatch.betSlip.betPlacementFailed({ errorCode: 'USER_SUSPENDED_BY_REGULATOR' });
          return mapBetLevelErrors([], betDefinitions, siteId);
        }

        if (hasBetGeneralFailure) {
          dispatch.betSlip.betPlacementFailed({ errorCode: 'GENERAL_FAILURE' });
          return mapBetLevelErrors(response.result, betDefinitions, siteId);
        }

        dispatch.betSlip.betPlacementFailed({
          errorCode: response.respCode,
          errorResponse: response
        });

        return mapBetLevelErrors(response.result, betDefinitions, siteId);
      }

      if (response.result) {
        betPlaced({
          response,
          betDefinitions,
          bettableOpportunities,
          opportunities: getAvailableSingleOpportunities(singleOpportunities),
          currency
        });
      }

      customerRef.current = uuidv4();

      // Dispatch receipt data to redux
      dispatch.betSlip.betPlacedSuccessfully({
        opportunities: bettableOpportunities,
        formValues,
        response
      });
    } catch (e) {
      if (e instanceof RequestError) {
        betPlacementException({
          // @ts-ignore // TODO: fix on api consolidation
          exception: e,
          bettableOpportunities,
          opportunities: getAvailableSingleOpportunities(singleOpportunities)
        });
      }
      const statusCode = (e as RequestError).headers?.statusCode;

      if (!isNetworkOrGeneralFailure(e as Error)) {
        customerRef.current = uuidv4();
      }

      if (statusCode && typeof statusCode === 'number' && statusCode >= 300) {
        dispatch.betSlip.betPlacementFailed({ errorCode: 'EXCEPTION' });
        return;
      }

      dispatch.betSlip.betPlacementFailed({ errorCode: 'NETWORK_FAILURE' });
    } finally {
      dispatch.modal.close('/sports/bet-delay');
    }
  };
};

export default usePlaceBet;
